<main-header></main-header>
<main>
    <div class="container">
        <div class="top-part">
            <div class="breadcrumb">
                <import-navigator currentStep="outcomeColumn"></import-navigator>
                <div class="wrap-text">
                    <b>
                        Confirm calculation options
                    </b>
                    <span *ngIf="oldBaseLine && newBaseLine && canonical">
                        Variance calculation approach, and basis and ceiling for relative variance impact
                    </span>
                    <span *ngIf="!oldBaseLine || !newBaseLine || !canonical">
                        Basis and ceiling for relative variance impact
                    </span>
                </div>
            </div>
        </div>
        <div class="middle-part set-up">
            <div [ngClass]="{'loading': !dataLoaded, 'current-status':true}">
                <div class="left">
                    <div class="dot"></div>
                    <div class="dot"></div>
                    <div class="dot green"></div>
                </div>
                <div class="divider"></div>
                <div *ngIf="selectedColumn" class="right">
                    <div class="wrap">
                        Select your preferred variance calculation approach <help-link></help-link>
                        <div class="sub-text">
                            Note that in the formulas, <b>bold</b> indicates <b>{{newControlValue}}</b> (e.g. <span class="text-bracket text-bracket__gray bracket-gray bold">{{formCompNames[0]}}</span> = '{{formCompNames[0]}} {{newControlValue}}'), and <i>italics</i> indicates <b>{{oldControlValue}}</b> (e.g. <span
                                class="text-bracket text-bracket__gray bracket-gray italic">{{formCompNames[0]}}</span> = {{formCompNames[0]}} {{oldControlValue}}). Also note that
                            if the residual difference between overall <span class="text-bracket text-bracket__gray">{{outcomeName}}</span> variance and the sum of 
                            <ng-container *ngFor="let formCompName of formCompNames.slice(0, formCompNames.length - 2)">
                                <span class="text-bracket text-bracket__gray">{{formCompName}}</span>,
                            </ng-container>
                            <span class="text-bracket text-bracket__gray">{{formCompNames[formCompNames.length - 2]}}</span> and <span class="text-bracket text-bracket__gray">{{formCompNames[formCompNames.length - 1]}}</span> variance is not zero, it will be distributed
                            across
                            <ng-container *ngFor="let formCompName of formCompNames.slice(0, formCompNames.length - 2)">
                                <span class="text-bracket text-bracket__gray">{{formCompName}}</span>,
                            </ng-container>
                            <span class="text-bracket text-bracket__gray">{{formCompNames[formCompNames.length - 2]}}</span> and <span class="text-bracket text-bracket__gray">{{formCompNames[formCompNames.length - 1]}}</span>
                            variance proportionally to their absolute value.
                        </div>
                    </div>
                    <div *ngIf="oldBaseLine && newBaseLine && canonical" class="wrap-block">
                        <div class="wrap">
                            <div class="radio-column">
                                <form [formGroup]="baseLineForm">
                                    <div class="form-choice">
                                        <input type="radio" id="baseLine1" formControlName="baseLineFrmCtrl" name="baseLineFrmCtrl" [value]="2" class="form-choice-radio">
                                        <label class="form-choice-label" for="baseLine1">
                                            Canonical calculation <br>
                                            <small>
                                                This approach may ensure a zero residual difference. The drivers' evaluation sequence matters: use the arrows to confirm the sequence you'd like to use.
                                            </small>
                                        </label>
                                        <div *ngIf="baseLineForm.controls.baseLineFrmCtrl.value === VarianceFormulaBaselineType.Canonical" class="wrap-content">
                                            <div *ngFor="let formCompVar of canonical; let i = index" class="row-formula sub-text">
                                                <div class="first-part">
                                                    <div class="wrap-arrows">
                                                        <a *ngIf="i > 0" (click)="moveUp(i)" class="arrow-top">
                                                            <img src="assets/images/icon-arrow-top-bold-beige.svg" alt="">
                                                        </a>
                                                        <a *ngIf="i < canonical.length - 1" (click)="moveDown(i)" class="arrow-bottom">
                                                            <img src="assets/images/icon-arrow-top-bold-beige.svg" alt="">
                                                        </a>
                                                    </div>
                                                    <div class="wrap-text">
                                                        <span class="text-bracket">{{formCompVar.formCompName}}</span>
                                                        <small>variance</small>
                                                    </div>
                                                </div>
                                                <div class="second-part">
                                                    <b>=</b>
                                                    <div class="wrap-formulas">
                                                        <div>
                                                            <span *ngFor="let item of formCompVar.newFormulaPart" [ngClass]="{ 'text-bracket': item.type != FormulaItemType.Other, 'text-bracket__gray': item.type != FormulaItemType.Other, 'bracket-gray': item.type != FormulaItemType.Other, 'bold': item.type === FormulaItemType.NewFormComp, 'italic': item.type === FormulaItemType.OldFormComp }">{{item.text}}</span>
                                                            <small><b>minus</b></small>
                                                        </div>
                                                        <div>
                                                            <span *ngFor="let item of formCompVar.oldFormulaPart" [ngClass]="{ 'text-bracket': item.type != FormulaItemType.Other, 'text-bracket__gray': item.type != FormulaItemType.Other, 'bracket-gray': item.type != FormulaItemType.Other, 'bold': item.type === FormulaItemType.NewFormComp, 'italic': item.type === FormulaItemType.OldFormComp }">{{item.text}}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-choice">
                                        <input type="radio" id="baseLine2" formControlName="baseLineFrmCtrl" name="baseLineFrmCtrl" [value]="0" class="form-choice-radio">
                                        <label class="form-choice-label" for="baseLine2">
                                            '{{oldControlValue}}' baseline calculation <br>
                                            <small>
                                                Evaluate the drivers with a sequence-independent approach, using '{{oldControlValue}}' as a baseline
                                            </small>
                                        </label>
                                        <div *ngIf="baseLineForm.controls.baseLineFrmCtrl.value === VarianceFormulaBaselineType.Old" class="wrap-content">
                                            <div *ngFor="let formCompVar of oldBaseLine" class="row-formula sub-text">
                                                <div class="first-part">
                                                    <div class="wrap-text">
                                                        <span class="text-bracket">{{ formCompVar.formCompName }}</span>
                                                        <small>variance</small>
                                                    </div>
                                                </div>
                                                <div class="second-part">
                                                    <b>=</b>
                                                    <div class="wrap-formulas">
                                                        <div>
                                                            <span *ngFor="let item of formCompVar.newFormulaPart" [ngClass]="{ 'text-bracket': item.type != FormulaItemType.Other, 'text-bracket__gray': item.type != FormulaItemType.Other, 'bracket-gray': item.type != FormulaItemType.Other, 'bold': item.type === FormulaItemType.NewFormComp, 'italic': item.type === FormulaItemType.OldFormComp }">{{item.text}}</span>
                                                            <small><b>minus</b></small>
                                                        </div>
                                                        <div>
                                                            <span *ngFor="let item of formCompVar.oldFormulaPart" [ngClass]="{ 'text-bracket': item.type != FormulaItemType.Other, 'text-bracket__gray': item.type != FormulaItemType.Other, 'bracket-gray': item.type != FormulaItemType.Other, 'bold': item.type === FormulaItemType.NewFormComp, 'italic': item.type === FormulaItemType.OldFormComp }">{{item.text}}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-choice">
                                        <input type="radio" id="baseLine3" formControlName="baseLineFrmCtrl" name="baseLineFrmCtrl" [value]="1" class="form-choice-radio">
                                        <label class="form-choice-label" for="baseLine3">
                                            '{{newControlValue}}' baseline calculation <br>
                                            <small>
                                                Evaluate the drivers with a sequence-independent approach, using '{{newControlValue}}' as a baseline
                                            </small>
                                        </label>
                                        <div *ngIf="baseLineForm.controls.baseLineFrmCtrl.value === VarianceFormulaBaselineType.New" class="wrap-content">
                                            <div *ngFor="let formCompVar of newBaseLine" class="row-formula sub-text">
                                                <div class="first-part">
                                                    <div class="wrap-text">
                                                        <span class="text-bracket">{{ formCompVar.formCompName }}</span>
                                                        <small>variance</small>
                                                    </div>
                                                </div>
                                                <div class="second-part">
                                                    <b>=</b>
                                                    <div class="wrap-formulas">
                                                        <div>
                                                            <span *ngFor="let item of formCompVar.newFormulaPart" [ngClass]="{ 'text-bracket': item.type != FormulaItemType.Other, 'text-bracket__gray': item.type != FormulaItemType.Other, 'bracket-gray': item.type != FormulaItemType.Other, 'bold': item.type === FormulaItemType.NewFormComp, 'italic': item.type === FormulaItemType.OldFormComp }">{{item.text}}</span>
                                                            <small><b>minus</b></small>
                                                        </div>
                                                        <div>
                                                            <span *ngFor="let item of formCompVar.oldFormulaPart" [ngClass]="{ 'text-bracket': item.type != FormulaItemType.Other, 'text-bracket__gray': item.type != FormulaItemType.Other, 'bracket-gray': item.type != FormulaItemType.Other, 'bold': item.type === FormulaItemType.NewFormComp, 'italic': item.type === FormulaItemType.OldFormComp }">{{item.text}}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                    <div class="wrap">
                        Select the <i>denominator</i> to evaluate the <i>relative impact</i> of {{outcomeName}} variance <help-link helpId="24"></help-link>
                    </div>
                    <div class="wrap">
                        <b class="text-bracket">{{selectedColumn.name}}</b> is currently selected, click on another column to change, and choose an option to continue:
                    </div>
                    <div class="wrap">
                        <div class="radio-column">
                            <form [formGroup]="denominatorForm">
                                <div class="form-choice">
                                    <input type="radio" id="reference1" formControlName="referenceFrmCtrl" name="referenceFrmCtrl"
                                           [value]="0" class="form-choice-radio">
                                    <label class="form-choice-label" for="reference1">
                                        Use '{{selectedColumn.name}} {{oldControlValue}}' as denominator <help-link helpId="25"></help-link><br>
                                        <small>
                                            In an example case where {{newControlValue}} {{selectedColumn.name}} = 10 and {{oldControlValue}} {{selectedColumn.name}} = 0.1, with this
                                            option, relative {{outcomeName}} variance impact would be equal to ({{newControlValue}} {{selectedColumn.name}} -
                                            {{oldControlValue}} {{selectedColumn.name}}) / {{oldControlValue}} {{selectedColumn.name}} = (10 - 0.1) / 0.1 = 9900%
                                        </small>
                                    </label>
                                </div>
                                <div class="form-choice">
                                    <input type="radio" id="reference2" formControlName="referenceFrmCtrl" name="referenceFrmCtrl"
                                           [value]="1" class="form-choice-radio">
                                    <label class="form-choice-label" for="reference2">
                                        Use the average between '{{selectedColumn.name}} {{oldControlValue}}' and '{{selectedColumn.name}} {{newControlValue}}' as denominator <help-link helpId="26"></help-link><br>
                                        <small>
                                            In the same example above, with this option, relative {{outcomeName}} variance impact
                                            would be equal to ({{newControlValue}} {{selectedColumn.name}} - {{oldControlValue}} {{selectedColumn.name}}) / Average({{newControlValue}} {{selectedColumn.name}}, {{oldControlValue}}
                                            {{selectedColumn.name}}) = (10 - 0.1) / ((10 + 0.1) / 2) = 196%
                                        </small>
                                    </label>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div class="wrap">
                        Indicate the desired ceiling for <i>relative</i> variance impact, in case the denominator was zero or very small <help-link helpId="27"></help-link>:
                        <div class="input-with-units">
                            <input type="number" [(ngModel)]="maxPerc" min="100" max="99900" class="no-border" style="width: 80px!important;">
                            <span>%</span>
                        </div>
                    </div>
                    <div class="wrap">
                        <button (click)="save()" class="btn btn-green btn-small">Continue</button>
                    </div>
                </div>
            </div>

            <div class="table-data">
                <table>
                    <thead>
                    <tr>
                        <th *ngFor="let col of columns" (click)="selectColumn(col)" [ngClass]="{'eligible': col.isEligible, 'selected': col.isSelected}">
                             {{col.name}}
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr class="small-font">
                        <td *ngFor="let col of columns" [ngClass]="{'eligible': col.isEligible, 'selected': col.isSelected}">
                            <ng-container *ngIf="col.isNumerical">
                                <b class="green">#</b>,&nbsp;
                            </ng-container>
                            {{ displayRoughNum(col.uniqueValues) }}
                        </td>
                    </tr>
                    <tr>
                        <td *ngFor="let col of columns" [ngClass]="{'eligible': col.isEligible, 'cell-color': true}">
                            <ng-container *ngIf="col.someVals && col.someVals.length">
                                <div class="gray">
                                    {{col.someVals[0]}}
                                </div>
                            </ng-container>
                        </td>
                    </tr>
                    <tr>
                        <td *ngFor="let col of columns" [ngClass]="{'eligible': col.isEligible, 'cell-color': true}">
                            <ng-container *ngIf="col.someVals && col.someVals.length > 1">
                                <div class="gray">
                                    {{col.someVals[1]}}
                                </div>
                            </ng-container>
                        </td>
                    </tr>
                    <tr>
                        <td *ngFor="let col of columns" [ngClass]="{'eligible': col.isEligible, 'cell-color': true}">
                            <ng-container *ngIf="col.someVals && col.someVals.length > 2">
                                <div class="gray">
                                    {{col.someVals[2]}}
                                </div>
                            </ng-container>
                        </td>
                    </tr>
                    <tr>
                        <td *ngFor="let col of columns" [ngClass]="{'eligible': col.isEligible, 'cell-color': true}">
                            <ng-container *ngIf="col.someVals && col.someVals.length > 3">
                                <div class="gray">
                                    {{col.someVals[3]}}
                                </div>
                            </ng-container>
                        </td>
                    </tr>
                    <tr>
                        <td *ngFor="let col of columns" [ngClass]="{'eligible': col.isEligible, 'cell-color': true}">
                            <ng-container *ngIf="col.someVals && col.someVals.length > 4">
                                <div class="gray">
                                    {{col.someVals[4]}}
                                </div>
                            </ng-container>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

        </div>
    </div>
</main>
